<script setup lang="ts">
import { useMidi } from '@/composables/useMidi';
import { useThrottleFn } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, ref, useSlots, watch } from 'vue';
import { GameMode, useInjectGameEngine } from '../composables/useGameEngine';
import { useInjectSheetMusic } from '../composables/useSheetMusic';
import { useGameChromeVisibility } from '../composables/useGameChromeVisibility';
import { provideNoteAnnotations } from '../composables/useNoteAnnotations';
import GameAreaArcadeMode from './GameAreaArcadeMode.vue';
import GameAreaComboCounter from './GameAreaComboCounter.vue';
import GameAreaSheetMusic from './GameAreaSheetMusic.vue';
import GameAreaGameResults from './GameAreaGameResults.vue';
import GameAreaSettingsPane from './GameAreaSettingsPane.vue';
import AnnotationPane from './AnnotationPane.vue';
import GameAreaStatusBar from './GameAreaStatusBar.vue';
import GameControlBar from './GameControlBar.vue';
import AudioWaveform from './AudioWaveform.vue';
import SectionLoopSelector from './SectionLoopSelector.vue';
import ToolbarIconToggle from '@/components/shared/ToolbarIconToggle.vue';
import { PencilIcon, TrashIcon } from '@heroicons/vue/24/outline';
import type { PieceSection } from '@/types';
import type { Id } from '../../convex/_generated/dataModel';

const props = withDefaults(defineProps<{
  musicXmlFile?: File;
  audioFile?: File;
  // True while audioFile is still downloading. The sheet renders without waiting
  // for audio, so the loading overlay needs this to stay up until both are ready.
  audioPending?: boolean;
  pieceTempo?: number;
  canSeekProgress?: boolean;
  showGameResults?: boolean;
  playbackBar?: 'full' | 'settings-only' | 'hidden';
  musicalPieceId?: Id<'musicalPieces'>;
  /** Library name of the piece, shown as the status bar's song title. */
  pieceName?: string;
  adminSections?: readonly PieceSection[];
  loopViewOnly?: boolean;
  /** Classroom room code, shown in the status bar's centre slot. */
  // eslint-disable-next-line vue/require-default-prop
  roomCode?: string;
}>(), {
  audioPending: false,
  canSeekProgress: true,
  showGameResults: true,
  playbackBar: 'full',
  loopViewOnly: false,
});

const emit = defineEmits<{
  'loop-update': [data: { startMeasure: number | null; endMeasure: number | null; source: 'piece' | 'custom' | undefined }];
  'exit': [];
  'seek': [data: { timeMs: number; wasPlaying: boolean }];
  /**
   * Preview mode needs playback stopped before it can ask for confirmation.
   * Forwarded rather than handled here: a classroom host has to broadcast the
   * pause so participants stop with them, and only the view knows that.
   */
  'pause': [];
}>();

const showSectionSelector = defineModel<boolean>('showSectionSelector', { default: false });
const showSettingsPane = defineModel<boolean>('showSettingsPane', { default: false });

const sheetMusic = useInjectSheetMusic();
const gameEngine = useInjectGameEngine();
const midi = useMidi();
const slots = useSlots();
const chrome = useGameChromeVisibility();

// Annotations are keyed to a specific piece, so this only exists when one is
// loaded (never true for e.g. an admin preview with no saved piece). Safe to
// provide once at setup rather than reactively: both call sites (PieceGameView
// via the route, ClassroomGameView via the loading-gated v-else) remount
// GameArea whenever the piece changes, rather than swapping this prop live.
// `noteAnnotations` is only ever present/absent in lockstep with
// `props.musicalPieceId` — template gates that just toggle the annotations UI
// on/off (no annotation data involved) check `props.musicalPieceId` directly
// for that reason, reserving `noteAnnotations` for actually reading its state.
const noteAnnotations = props.musicalPieceId ? provideNoteAnnotations(props.musicalPieceId) : undefined;
const showAnnotationPane = ref(false);

const audioWaveform = ref<InstanceType<typeof AudioWaveform>>();

watch(midi.notePlayed, (notePlayed) => {
  if (gameEngine.isPlaying.value){
    gameEngine.checkNoteHit(notePlayed.time, notePlayed.note);
  }
});

const resultsDismissed = ref(false);
watch(() => gameEngine.isPlaying.value, (isPlaying) => {
  if (isPlaying) resultsDismissed.value = false;
  // Hides the chrome the instant play starts and brings it back (to stay) on
  // pause — see useGameChromeVisibility for why this isn't gesture-driven.
  chrome.notifyPlaying(isPlaying);
});

watch(sheetMusic.isLoaded, (isLoaded) => {
  if (isLoaded) {
    setTimeout(() => {
      gameEngine.initGame();
    }, 0);
  }
});

watch(audioWaveform, (waveform) => {
  gameEngine.audioWaveform.value = waveform;
}, { immediate: true });

const audioLoopSection = computed(() => {
  if (gameEngine.loopStartTime.value === null || gameEngine.loopEndTime.value === null) {
    return null;
  }
  return {
    start: gameEngine.loopStartTime.value - (gameEngine.audioOffset.value || 0),
    end: gameEngine.loopEndTime.value - (gameEngine.audioOffset.value || 0),
  };
});
const isWaveformLoading = computed(() => !!gameEngine.audioWaveform.value?.isLoading);

watch([() => audioWaveform.value?.getAudioDurationInSecond(), gameEngine.audioOffset], ([audioDuration, offset]) => {
  gameEngine.audioDuration.value = audioDuration
    ? audioDuration * 1000 + (offset || 0)
    : undefined;
}, { immediate: true });

onBeforeUnmount(() => {
  if (gameEngine.isPlaying.value) {
    gameEngine.stopPlayback();
  }
  window.removeEventListener('keydown', handleKeydown);
});

const isArcadeMode = computed(() => gameEngine.gameMode.value === GameMode.Arcade);

const handleRestart = () => {
  gameEngine.initGame();
  gameEngine.startGame();
};

const handleTogglePlay = () => {
  if (gameEngine.isPlaying.value) {
    gameEngine.stopPlayback();
  } else {
    gameEngine.revertNoteHitsAtOrAfter(gameEngine.currentTime.value);
    gameEngine.startGame();
  }
};

// Spacebar play/pause, YouTube-style — ignored while typing into a text
// field so it doesn't hijack normal spacebar use there. preventDefault runs
// for every other target (including a focused button) so the browser's own
// "space activates the focused button" behavior never double-fires this.
const handleKeydown = (event: KeyboardEvent) => {
  if (event.code !== 'Space' && event.key !== ' ') return;
  const target = event.target as HTMLElement | null;
  const tag = target?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
  event.preventDefault();
  handleTogglePlay();
};

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

// Any tap/click anywhere in the game area counts as "show me the chrome" —
// capture phase so it fires before a button's own click handler, and doesn't
// need to know or care which element was actually hit.
const handlePointerActivity = () => chrome.notePointerDown();
// Desktop only really has mouse-move to go on; throttled so a long hover
// doesn't spam the idle-hide timer on every pixel of travel.
const handleMouseMove = useThrottleFn(() => chrome.reveal(), 250);

// A click anywhere that isn't a real control (button/input/link/…) or a
// surface that already runs its own tap-vs-drag gesture (the sheet music's
// scrub, the progress bar's seek, the settings pane's own dismiss logic) is
// the YouTube-style "toggle the controls" tap. Deliberately NOT restricted to
// target === currentTarget (that broke the moment any wrapper div sat
// between the click and a bar's root) — `closest()` walking up from the
// actual click target is robust no matter how deep the markup nests.
// Safe to call redundantly alongside the sheet's own onTap: handleTap keys
// off the visibility snapshot from the gesture's pointerdown, not off how
// many times it's invoked, so calling it twice for one gesture is a no-op.
const CHROME_TAP_EXEMPT_SELECTOR = 'button, a, input, select, textarea, [role="slider"], .sheet-music-container, .settings-pane, .annotation-pane, [data-chrome-tap-exempt]';
const handleBackgroundClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (target.closest(CHROME_TAP_EXEMPT_SELECTOR)) return;
  chrome.handleTap();
};
</script>

<template>
  <div
    class="w-full h-full relative min-h-0"
    tabindex="-1"
    @pointerdown.capture="handlePointerActivity"
    @mousemove="handleMouseMove"
    @click="handleBackgroundClick"
  >
    <!-- Hidden AudioWaveform component -->
    <div class="hidden">
      <AudioWaveform
        v-if="audioFile"
        ref="audioWaveform"
        v-model:audio-offset="gameEngine.audioOffset.value"
        :loop-enabled="gameEngine.loopEnabled.value"
        :audio-file="audioFile"
        :timing-map="sheetMusic.measureTimingMap.value"
        :current-play-time="gameEngine.currentTime"
        :is-playing="gameEngine.isPlaying.value"
        :loop-section="audioLoopSection"
        :playback-rate="gameEngine.playbackSpeed.value"
      />
    </div>

    <!-- Full-bleed layer: sheet music behind everything else, so the status
         bar / control bar below can float over it as translucent scrims
         instead of pushing it into a shorter middle strip. -->
    <div class="absolute inset-0 overflow-hidden bg-background flex">
      <div class="flex-1 h-full relative overflow-hidden">
        <div class="game-overlay-top-right">
          <slot name="overlay-top-right" />
        </div>
        <div class="game-overlay-bottom-left">
          <slot name="overlay-bottom-left" />
        </div>
        <!-- Sheet music display (shown in sheet music mode) -->
        <Transition name="fade">
          <GameAreaSheetMusic
            v-show="!isArcadeMode"
            :music-xml-file="musicXmlFile"
            :audio-pending="props.audioPending"
            :sections="props.adminSections ? [...props.adminSections] : undefined"
            :can-seek="props.canSeekProgress"
            @seek="emit('seek', $event)"
          />
        </Transition>
        <!-- Arcade mode display (shown in arcade mode).
             TODO: the sheet-music / arcade switch was removed from the status
             bar, so gameMode can no longer leave GameMode.SheetMusic and this
             branch is unreachable. Everything behind it — GameAreaArcadeMode,
             gameEngine.gameMode, the persisted 'gameMode' storage key — is left
             untouched on purpose; decide whether arcade mode comes back behind a
             new entry point or gets deleted outright. -->
        <Transition name="fade">
          <GameAreaArcadeMode v-if="sheetMusic.isLoaded.value && isArcadeMode" />
          <div v-else />
        </Transition>
        <GameAreaComboCounter v-if="gameEngine.comboCount.value > 0" />
      </div>
      <!-- Floats over the score (see .annotation-pane-overlay) instead of
           shrinking its column, so the score never shifts or reflows while
           the pane's open. -->
      <AnnotationPane
        v-if="props.musicalPieceId"
        v-model:open="showAnnotationPane"
        class="annotation-pane-overlay"
      />
      <GameAreaSettingsPane
        v-model:open="showSettingsPane"
        class="settings-pane-overlay"
        :show-playback-settings="props.playbackBar !== 'settings-only'"
        @pause="emit('pause')"
      />
    </div>

    <!-- Annotation editing entry point — fades with the rest of the chrome. -->
    <ToolbarIconToggle
      v-if="props.musicalPieceId"
      class="annotation-toggle-button chrome-fade"
      :class="{ 'chrome-fade--hidden': !chrome.visible.value }"
      :active="showAnnotationPane"
      aria-label="Edit note annotations"
      :aria-expanded="showAnnotationPane"
      @click="showAnnotationPane = !showAnnotationPane"
    >
      <PencilIcon class="w-5 h-5" />
    </ToolbarIconToggle>

    <!-- Floating drop-to-delete target, shown only while a cell is in flight. -->
    <Transition name="fade">
      <div
        v-if="noteAnnotations?.isDragging.value"
        :ref="(el) => { if (noteAnnotations) noteAnnotations.trashEl.value = el as HTMLElement | null }"
        class="annotation-trash-zone"
        :class="{ 'annotation-trash-zone--armed': noteAnnotations?.isPointerOverTrash.value }"
      >
        <TrashIcon class="w-6 h-6" />
      </div>
    </Transition>

    <!-- Game status bar: floats over the sheet, see .game-status-bar. -->
    <GameAreaStatusBar
      :piece-tempo="props.pieceTempo"
      :can-seek-progress="props.canSeekProgress"
      :musical-piece-id="props.musicalPieceId"
      :piece-name="props.pieceName"
      :room-code="props.roomCode"
      @seek="emit('seek', $event)"
    >
      <template #header-left>
        <slot name="header-left" />
      </template>
    </GameAreaStatusBar>

    <GameControlBar
      v-if="props.playbackBar !== 'hidden'"
      v-model:show-settings-pane="showSettingsPane"
      v-model:show-section-selector="showSectionSelector"
      :mode="props.playbackBar === 'settings-only' ? 'settings-only' : 'full'"
      :is-playing="gameEngine.isPlaying.value"
      :actions-disabled="isWaveformLoading"
      :musical-piece-id="props.musicalPieceId"
      :loop-view-only="props.loopViewOnly"
      @restart="handleRestart"
      @toggle-play="handleTogglePlay"
      @loop-update="emit('loop-update', $event)"
    />

    <!-- Game results dialog -->
    <Teleport to="body">
      <Transition name="fade">
        <GameAreaGameResults
          v-if="props.showGameResults && !resultsDismissed && !gameEngine.previewMode.value && !gameEngine.isPlaying.value && gameEngine.progress.value >= 100"
          :musical-piece-id="props.musicalPieceId"
          @exit="$emit('exit')"
          @close="resultsDismissed = true"
        >
          <template
            v-if="slots['results-actions']"
            #actions
          >
            <slot name="results-actions" />
          </template>
        </GameAreaGameResults>
      </Transition>
    </Teleport>

    <!-- Section loop selector. Mounted as soon as the sheet music is available,
         not on open: rendering the score takes ~0.5s, and this is a frequently
         used control. It stays hidden (not unmounted) until showSectionSelector. -->
    <!-- Indecisive: eager render this component or not?  
     may consume too much memory? 
     i.e. put showSectionSelector in v-if or not? -->
    <SectionLoopSelector
      v-if="props.musicalPieceId && musicXmlFile && showSectionSelector"
      :open="showSectionSelector"
      :music-xml-file="musicXmlFile"
      :admin-sections="props.adminSections"
      :musical-piece-id="props.musicalPieceId"
      :loop-view-only="props.loopViewOnly"
      @close="showSectionSelector = false"
      @apply="emit('loop-update', $event)"
    />
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.game-overlay-top-right {
  position: absolute;
  top: 8px;
  right: 8px;
}

/* Sits above the sheet music and the status bar (z-index 15), but below
   GameControlBar (z-index 20) in case a future caller ever pairs this with a
   visible control bar. */
.game-overlay-bottom-left {
  position: absolute;
  bottom: 68px;
  left: 8px;
  z-index: 16;
}

/*
 * Was a flex sibling that shrank the score's column when opened; the score is
 * now full-bleed (see the template), so the pane instead slides in from the
 * right and floats over it, matching the sheet music container's own
 * .settings-pane width transition inside.
 */
.settings-pane-overlay {
  position: absolute;
  top: 0;
  right: 0;
  /* Must clear the mobile "go back" button that PieceGameView/ClassroomGameView
     render outside GameArea entirely (position: absolute, z-index: 30), plus
     MainNav's mobile hamburger button/backdrop/sidenav (z-index 30/40) and the
     connection indicator (z-index 30) — otherwise those paint over this pane
     while it's open. */
  z-index: 46;
}

.annotation-pane-overlay {
  position: absolute;
  top: 0;
  left: 0;
  /* Above MainNav's mobile hamburger button/backdrop/sidenav (z-index
     30/40) and the connection indicator (z-index 30) — this pane is a modal
     editing surface, so nothing else should be able to paint over it while
     it's open. */
  z-index: 45;
}

/* Sits below the settings pane/hamburger nav's corner but above the status
   bar, and low enough to clear the mobile hamburger button (fixed top-left,
   z-index 30) so the two never overlap. Circle/background/shadow chrome
   itself comes from ToolbarIconToggle — this only positions it. */
.annotation-toggle-button {
  position: absolute;
  top: 120px;
  left: 16px;
  z-index: 17;
}

/* Fades with the game's status/control bars — see useGameChromeVisibility. */
.chrome-fade {
  transition: opacity 0.25s ease;
}

.chrome-fade--hidden {
  opacity: 0;
  pointer-events: none;
}

.annotation-trash-zone {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) scale(1);
  z-index: 26;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: var(--color-error);
  color: white;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
  /* The house ease — a confident scale-pop when a dragged annotation is
     hovering over it, same curve as every other transform in this app. */
  transition: transform 0.3s cubic-bezier(0.33, 1, 0.68, 1), box-shadow 0.3s cubic-bezier(0.33, 1, 0.68, 1);
}

.annotation-trash-zone--armed {
  transform: translateX(-50%) scale(1.15);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.45);
}

.annotation-trash-zone--armed :deep(svg) {
  animation: annotation-trash-shake 0.3s cubic-bezier(0.33, 1, 0.68, 1);
}

@keyframes annotation-trash-shake {
  0%, 100% { transform: rotate(0deg); }
  30% { transform: rotate(-12deg); }
  60% { transform: rotate(10deg); }
}
</style>
